import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { BatchGetGroupThreadStatusIO } from '../types';

type GroupThreadStatusRequest = Omit<BatchGetGroupThreadStatusIO['input']['requests'][number], 'threadId'>;

const hashRequest = ({ personPhones, locationPhone, locationId }: GroupThreadStatusRequest): string =>
  `${(personPhones ?? []).join(',')}|${locationId}|${locationPhone}`;

const convertToArray = (map: Record<string, string[]>): { locationId: string; locationPhones: string[] }[] => {
  const entries = Object.entries(map);
  return entries.map(([locationId, locationPhones]) => ({ locationId, locationPhones }));
};

export type CalculatePossibleGroupThreadStatusRequestsArgs = {
  personPhones: string[];
  locationPhonesByLocation: { locationId: string; locationPhones: string[] }[] | Record<string, string[]>;
};

/**
 * Takes in various lists that map to parts of a `BatchGetGroupThreadStatus` request and returns every possible combination
 */
export const calculatePossibleGroupThreadStatusRequests = ({
  personPhones,
  locationPhonesByLocation,
}: CalculatePossibleGroupThreadStatusRequestsArgs): BatchGetGroupThreadStatusIO['input']['requests'] => {
  const normalizedPersonPhones = Array.from(new Set(personPhones.map(formatPhoneNumberE164))).sort();
  if (normalizedPersonPhones.length < 2) {
    return [];
  }

  const locationPhonesByLocationArray = Array.isArray(locationPhonesByLocation)
    ? locationPhonesByLocation
    : convertToArray(locationPhonesByLocation);

  return locationPhonesByLocationArray.reduce<{
    requestHashes: Set<string>;
    requests: BatchGetGroupThreadStatusIO['input']['requests'];
  }>(
    (acc, { locationId, locationPhones }) => {
      const normalizedLocationPhones = Array.from(new Set(locationPhones.map(formatPhoneNumberE164)));

      normalizedLocationPhones.forEach((locationPhone) => {
        const request = {
          personPhones: normalizedPersonPhones,
          locationId,
          locationPhone,
        };
        const hash = hashRequest(request);
        if (!acc.requestHashes.has(hash)) {
          acc.requests.push(request);
          acc.requestHashes.add(hash);
        }
      });

      return acc;
    },
    { requestHashes: new Set<string>(), requests: [] }
  ).requests;
};
